import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { AlertCircle, FlaskConical, Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { ApiError } from "@shared-core/data/api/errors";

/**
 * Opens a testing preview by its environment id (docs/features/quote-to-invoice-e2e-testing/06 §4.1): the share
 * URL `cis preview` prints. The signed-in staff member's token mints a one-time exchange for that one preview, and
 * the browser moves there; the preview then asks them to sign in with the same staff account.
 */
export function TestingPreviewOpenPage() {
  const { environmentId = "" } = useParams<{ environmentId: string }>();
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adapter
      .openTestingPreview(environmentId)
      .then((entry) => {
        if (!cancelled) capabilities.redirectTo(entry.exchange_url);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setFailure(
            "This testing preview has stopped or expired. Ask for a new one.",
          );
        } else if (err instanceof ApiError && err.status === 403) {
          setFailure("Your account cannot open testing previews.");
        } else {
          setFailure(
            "The testing preview could not be opened. Try again in a minute.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, capabilities, environmentId]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-muted-foreground">
      {failure ? (
        <>
          <AlertCircle className="size-10 opacity-60" />
          <p className="text-lg" role="alert">
            {failure}
          </p>
        </>
      ) : (
        <>
          <FlaskConical className="size-10 opacity-60" />
          <p className="flex items-center gap-2 text-lg">
            <Loader2 className="size-5 animate-spin" />
            Opening the testing preview…
          </p>
        </>
      )}
    </div>
  );
}
